/**
 * Global cost assumptions for the Policy Impact Tool — energy prices, tech costs, financial, transport tech.
 * Drives Portfolio Cost & Savings, Decision Summary break-even text, and Scenario Cost Comparison.
 */
import React from 'react';
import { type CostAssumptions } from '../../services/costAssumptions';
import { ICE_MPG, EV_KWH_PER_MILE } from '../../services/transportCostModel';

const DEFAULT_ICE_MPG = ICE_MPG;
const DEFAULT_EV_KWH_PER_MILE = EV_KWH_PER_MILE;

export interface PolicyToolCostAssumptionsSectionProps {
  assumptions: CostAssumptions;
  setAssumptions: React.Dispatch<React.SetStateAction<CostAssumptions>>;
  iceMpg: number;
  setIceMpg: React.Dispatch<React.SetStateAction<number>>;
  evKwhPerMile: number;
  setEvKwhPerMile: React.Dispatch<React.SetStateAction<number>>;
  /** Resolved location electricity (price + short source label from parent fetch). */
  electricityPriceContext: { pricePerKwh: number; source: string };
  /** Reset tables to defaults; parent should re-apply resolved electricity price for current location. */
  onResetCostAssumptions: () => void;
}

export const PolicyToolCostAssumptionsSection: React.FC<PolicyToolCostAssumptionsSectionProps> = ({
  assumptions,
  setAssumptions,
  iceMpg,
  setIceMpg,
  evKwhPerMile,
  setEvKwhPerMile,
  electricityPriceContext,
  onResetCostAssumptions,
}) => {
  return (
    <section
      id="policy-tool-cost-assumptions"
      className="policy-cost-assumptions-section"
      style={{ marginTop: 28, paddingTop: 20, borderTop: '1px solid var(--border-color)' }}
    >
      <details
        style={{ border: '1px solid var(--border-color)', borderRadius: 8, background: 'var(--section-bg-alt)' }}
      >
        <summary
          style={{ cursor: 'pointer', padding: '12px 16px', fontWeight: 600, fontSize: '1rem', listStyle: 'none' }}
        >
          <span style={{ marginRight: 8 }}>▶</span> Cost Assumptions
        </summary>
        <div style={{ padding: '0 16px 16px', borderTop: '1px solid var(--border-color)' }}>
      <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)', marginTop: 12, marginBottom: 14 }}>
        Prices and technology inputs apply to <strong>portfolio buildings &amp; transport cost</strong>, the{' '}
        <strong>decision summary</strong> (break-even electricity vs gas), and <strong>scenario cost comparison</strong>{' '}
        across locations. Per-location price overrides are in <strong>Scenario Cost Comparison</strong> (section above).
      </p>
      <p style={{ fontSize: '0.8125rem', color: 'var(--text-secondary)', marginBottom: 16 }}>
        Electricity for this location updates when utility data loads ({electricityPriceContext.source}); you can override
        below. Other values update analysis after a short delay.
      </p>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 16, marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: 6 }}>Energy prices</div>
          <label style={{ display: 'block', fontSize: '0.8125rem', marginBottom: 2 }}>Electricity ($/kWh) — average</label>
          <input
            type="number"
            min={0}
            step={0.01}
            value={assumptions.electricityPricePerKwh}
            onChange={(e) => setAssumptions((a) => ({ ...a, electricityPricePerKwh: Number(e.target.value) || 0 }))}
            style={{ width: '100%', maxWidth: 100, padding: '4px 8px' }}
          />
          <label style={{ display: 'block', fontSize: '0.8125rem', marginTop: 6, marginBottom: 2 }}>Natural gas ($/therm)</label>
          <input
            type="number"
            min={0}
            step={0.01}
            value={assumptions.naturalGasPricePerTherm}
            onChange={(e) => setAssumptions((a) => ({ ...a, naturalGasPricePerTherm: Number(e.target.value) || 0 }))}
            style={{ width: '100%', maxWidth: 100, padding: '4px 8px' }}
          />
          <label style={{ display: 'block', fontSize: '0.8125rem', marginTop: 6, marginBottom: 2 }}>Gasoline ($/gallon)</label>
          <input
            type="number"
            min={0}
            step={0.1}
            value={assumptions.gasolinePricePerGallon}
            onChange={(e) => setAssumptions((a) => ({ ...a, gasolinePricePerGallon: Number(e.target.value) || 0 }))}
            style={{ width: '100%', maxWidth: 100, padding: '4px 8px' }}
          />
        </div>
        <div>
          <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: 6 }}>Technology costs</div>
          <label style={{ display: 'block', fontSize: '0.8125rem', marginBottom: 2 }}>Res. heat pump incremental ($/unit)</label>
          <input
            type="number"
            min={0}
            step={500}
            value={assumptions.resHeatPumpIncrementalCostPerUnit}
            onChange={(e) => setAssumptions((a) => ({ ...a, resHeatPumpIncrementalCostPerUnit: Number(e.target.value) || 0 }))}
            style={{ width: '100%', maxWidth: 100, padding: '4px 8px' }}
          />
          <label style={{ display: 'block', fontSize: '0.8125rem', marginTop: 6, marginBottom: 2 }}>Com. heat pump incremental ($/equiv. unit)</label>
          <input
            type="number"
            min={0}
            step={1000}
            value={assumptions.comHeatPumpIncrementalCostPerUnit}
            onChange={(e) => setAssumptions((a) => ({ ...a, comHeatPumpIncrementalCostPerUnit: Number(e.target.value) || 0 }))}
            style={{ width: '100%', maxWidth: 100, padding: '4px 8px' }}
          />
          <label style={{ display: 'block', fontSize: '0.8125rem', marginTop: 6, marginBottom: 2 }}>Avg therms per res. unit</label>
          <input
            type="number"
            min={1}
            step={50}
            value={assumptions.avgThermsPerResUnit}
            onChange={(e) => setAssumptions((a) => ({ ...a, avgThermsPerResUnit: Number(e.target.value) || 1 }))}
            style={{ width: '100%', maxWidth: 100, padding: '4px 8px' }}
          />
          <label style={{ display: 'block', fontSize: '0.8125rem', marginTop: 6, marginBottom: 2 }}>Avg therms per com. equiv. unit</label>
          <input
            type="number"
            min={1}
            step={500}
            value={assumptions.avgThermsPerComEquivalentUnit}
            onChange={(e) => setAssumptions((a) => ({ ...a, avgThermsPerComEquivalentUnit: Number(e.target.value) || 1 }))}
            style={{ width: '100%', maxWidth: 100, padding: '4px 8px' }}
          />
          <label style={{ display: 'block', fontSize: '0.8125rem', marginTop: 6, marginBottom: 2 }}>EV incremental ($)</label>
          <input
            type="number"
            min={0}
            step={500}
            value={assumptions.evIncrementalCost}
            onChange={(e) => setAssumptions((a) => ({ ...a, evIncrementalCost: Number(e.target.value) || 0 }))}
            style={{ width: '100%', maxWidth: 100, padding: '4px 8px' }}
          />
          <label style={{ display: 'block', fontSize: '0.8125rem', marginTop: 6, marginBottom: 2 }}>Charger cost per EV ($)</label>
          <input
            type="number"
            min={0}
            step={100}
            value={assumptions.chargerCostPerEv}
            onChange={(e) => setAssumptions((a) => ({ ...a, chargerCostPerEv: Number(e.target.value) || 0 }))}
            style={{ width: '100%', maxWidth: 100, padding: '4px 8px' }}
          />
          <label style={{ display: 'block', fontSize: '0.8125rem', marginTop: 6, marginBottom: 2 }}>Res. retrofit ($/home)</label>
          <input
            type="number"
            min={0}
            step={500}
            value={assumptions.weatherizationCostPerParticipatingHome}
            onChange={(e) => setAssumptions((a) => ({ ...a, weatherizationCostPerParticipatingHome: Number(e.target.value) || 0 }))}
            style={{ width: '100%', maxWidth: 100, padding: '4px 8px' }}
          />
          <label style={{ display: 'block', fontSize: '0.8125rem', marginTop: 6, marginBottom: 2 }}>Com. retrofit ($/est.)</label>
          <input
            type="number"
            min={0}
            step={1000}
            value={assumptions.commercialRetrofitCostPerParticipatingEstablishment}
            onChange={(e) =>
              setAssumptions((a) => ({ ...a, commercialRetrofitCostPerParticipatingEstablishment: Number(e.target.value) || 0 }))
            }
            style={{ width: '100%', maxWidth: 100, padding: '4px 8px' }}
          />
          <label style={{ display: 'block', fontSize: '0.8125rem', marginTop: 6, marginBottom: 2 }}>Res. reach code ($/unit)</label>
          <input
            type="number"
            min={0}
            step={500}
            value={assumptions.residentialReachCodeCostPerNewUnit}
            onChange={(e) => setAssumptions((a) => ({ ...a, residentialReachCodeCostPerNewUnit: Number(e.target.value) || 0 }))}
            style={{ width: '100%', maxWidth: 100, padding: '4px 8px' }}
          />
          <label style={{ display: 'block', fontSize: '0.8125rem', marginTop: 6, marginBottom: 2 }}>Com. reach code ($/unit)</label>
          <input
            type="number"
            min={0}
            step={1000}
            value={assumptions.commercialReachCodeCostPerNewUnit}
            onChange={(e) => setAssumptions((a) => ({ ...a, commercialReachCodeCostPerNewUnit: Number(e.target.value) || 0 }))}
            style={{ width: '100%', maxWidth: 100, padding: '4px 8px' }}
          />
        </div>
        <div>
          <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: 6 }}>Financial</div>
          <label style={{ display: 'block', fontSize: '0.8125rem', marginBottom: 2 }}>Discount rate (%)</label>
          <input
            type="number"
            min={0}
            max={20}
            step={0.5}
            value={assumptions.discountRate * 100}
            onChange={(e) => setAssumptions((a) => ({ ...a, discountRate: (Number(e.target.value) || 0) / 100 }))}
            style={{ width: '100%', maxWidth: 100, padding: '4px 8px' }}
          />
          <label style={{ display: 'block', fontSize: '0.8125rem', marginTop: 6, marginBottom: 2 }}>Heat pump rebate ($)</label>
          <input
            type="number"
            min={0}
            step={500}
            value={assumptions.heatPumpRebate}
            onChange={(e) => setAssumptions((a) => ({ ...a, heatPumpRebate: Number(e.target.value) || 0 }))}
            style={{ width: '100%', maxWidth: 100, padding: '4px 8px' }}
          />
          <label style={{ display: 'block', fontSize: '0.8125rem', marginTop: 6, marginBottom: 2 }}>Weatherization rebate ($)</label>
          <input
            type="number"
            min={0}
            step={500}
            value={assumptions.weatherizationRebate}
            onChange={(e) => setAssumptions((a) => ({ ...a, weatherizationRebate: Number(e.target.value) || 0 }))}
            style={{ width: '100%', maxWidth: 100, padding: '4px 8px' }}
          />
          <label style={{ display: 'block', fontSize: '0.8125rem', marginTop: 6, marginBottom: 2 }}>Com. retrofit rebate ($)</label>
          <input
            type="number"
            min={0}
            step={500}
            value={assumptions.commercialRetrofitRebate}
            onChange={(e) => setAssumptions((a) => ({ ...a, commercialRetrofitRebate: Number(e.target.value) || 0 }))}
            style={{ width: '100%', maxWidth: 100, padding: '4px 8px' }}
          />
        </div>
        <div>
          <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-primary)', marginBottom: 6 }}>Transport tech</div>
          <label style={{ display: 'block', fontSize: '0.8125rem', marginBottom: 2 }}>ICE (mpg)</label>
          <input
            type="number"
            min={10}
            max={60}
            step={1}
            value={iceMpg}
            onChange={(e) => setIceMpg(Number(e.target.value) || DEFAULT_ICE_MPG)}
            style={{ width: '100%', maxWidth: 100, padding: '4px 8px' }}
          />
          <label style={{ display: 'block', fontSize: '0.8125rem', marginTop: 6, marginBottom: 2 }}>EV (kWh/mile)</label>
          <input
            type="number"
            min={0.2}
            max={0.5}
            step={0.05}
            value={evKwhPerMile}
            onChange={(e) => setEvKwhPerMile(Number(e.target.value) || DEFAULT_EV_KWH_PER_MILE)}
            style={{ width: '100%', maxWidth: 100, padding: '4px 8px' }}
          />
        </div>
      </div>

      <button type="button" onClick={onResetCostAssumptions} style={{ padding: '6px 12px', fontSize: '0.8125rem' }}>
        Reset cost assumptions to defaults
      </button>
        </div>
      </details>
    </section>
  );
};

export default PolicyToolCostAssumptionsSection;
